import {
  GatewayAssujetti,
  GatewayConformite,
  GatewayContact,
} from "@conformite/gateway";
import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import {
  Form,
  InputButtonGroup,
  InputSelect,
  InputText,
  InputTextArea,
  useFormConfig,
  Button,
  displayErrorToast,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormNotice,
  FormRow,
  FormTitle,
  SelectOption,
  InfoCard,
} from "@endya-conformite/ui-kit";
import { useEffect, useMemo, useRef } from "react";
import { recordToSelectOptions } from "@src/adhesion/AdhesionData/adhesionData";
import { useAssujetti } from "@src/store/store.assujetti";
import { useEntreprises } from "@src/store/store.entreprise";
import { Assujetti } from "@src/store/store.definition";
import ArrowRightIcon from "@endya-conformite/ui-kit/src/assets/icons/arrow-right.svg?react";
import {
  AxeConformiteAssujettiLabels,
  axeToAvancementField,
  axeToStep,
  RoleAssujettiLabels,
} from "@src/assujettis/AssujettiConsts";
import { GatewayAssujettiApi } from "@src/api/assujetti.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { useIsAllowed } from "@src/session/useIsAllowed";
import styles from "./AssujettiInfoForm.module.scss";
import { ReadonlyFieldSet } from "../AssujettiParcoursConformite/Components/ReadonlyFieldSet";
import { getAvailableStepsConformite } from "../AssujettiParcoursConformite/AssujettiParcoursConformite";
import {
  AssujettiInfoFormInput,
  axeFormFields,
  axesToDefaultValues,
  getOptionalAxesFromValues,
} from "./AssujettiInfoForm.definition";

function arraysEqual(a: string[], b: string[]): boolean {
  return [...a].sort().join(",") === [...b].sort().join(",");
}

const assujettiInfoSchema = zod
  .object({
    id: zod.string().optional(),
    name: zod.string().nonempty(),
    surname: zod.string().nonempty(),
    role: zod.nativeEnum(GatewayAssujetti.RoleAssujetti),
    email: zod
      .union([zod.string().email().optional(), zod.literal("")])
      .transform((val) => (val === "" ? undefined : val)),
    sirenCompany: zod.string().nonempty(),
    optionalAxesDescription: zod.string().optional(),
    honorabilite: zod.boolean().optional(),
    formationContinueIas: zod.boolean().optional(),
    formationContinueIobsp: zod.boolean().optional(),
    capaciteProfessionnelleIas: zod.boolean().optional(),
    capaciteProfessionnelleIobsp: zod.boolean().optional(),
  })
  .transform((data) => {
    const optionalAxes = getOptionalAxesFromValues(data);
    return {
      id: data.id,
      name: data.name,
      surname: data.surname,
      role: data.role,
      email: data.email,
      sirenCompany: data.sirenCompany,
      optionalAxes: optionalAxes.length > 0 ? optionalAxes : undefined,
      optionalAxesDescription: data.optionalAxesDescription,
    };
  })
  .refine(
    (data) =>
      !data.optionalAxes?.length ||
      (data.optionalAxesDescription &&
        data.optionalAxesDescription.trim().length > 0),
    {
      message:
        "La justification est requise lorsque des axes optionnels sont sélectionnés ou modifiés",
      path: ["optionalAxesDescription"],
    }
  );

export type AssujettiInfoFormValues = zod.infer<typeof assujettiInfoSchema>;

export function useMyCompaniesOptions() {
  const { entreprisesBySiren } = useEntreprises();
  return useMemo<SelectOption<string>[]>(
    () =>
      entreprisesBySiren
        ? Object.values(entreprisesBySiren).map((e) => ({
            label: e.raisonSociale || "",
            value: e.siren,
          }))
        : [],
    [entreprisesBySiren]
  );
}

function computeAvancement(
  assujetti: Assujetti | null,
  submittedAssujetti: { id: string; millesime: number },
  newOptionalAxes: GatewayConformite.AxeConformiteAssujetti[]
) {
  const base = assujetti
    ? { ...assujetti.avancement }
    : {
        idAssujetti: submittedAssujetti.id,
        millesime: submittedAssujetti.millesime,
        honorabilite: null,
        formationContinueIas: null,
        formationContinueIobsp: null,
        capaciteProfessionnelleIas: null,
        capaciteProfessionnelleIobsp: null,
      };

  newOptionalAxes.forEach((axe) => {
    const field = axeToAvancementField[axe];
    if (field) base[field] = 100;
  });

  (assujetti?.optionalAxes ?? [])
    .filter((axe) => !newOptionalAxes.includes(axe))
    .forEach((axe) => {
      const field = axeToAvancementField[axe];
      if (field) base[field] = null;
    });

  return base;
}

// --- Component ---

type AssujettiInfoFormProps = {
  afterSubmit: (
    submittedAssujetti: Assujetti,
    shouldCompleteConformite: boolean
  ) => void;
  assujetti: Assujetti | null;
  millesime: number;
  readonly?: boolean;
  defaultSiren?: string;
};

const obligatoireOptionnelOptions = [
  { label: "Obligatoire", value: false },
  { label: "Optionnel", value: true },
];

export function AssujettiInfoForm({
  afterSubmit,
  assujetti,
  millesime,
  readonly,
  defaultSiren,
}: AssujettiInfoFormProps) {
  const companiesOption = useMyCompaniesOptions();
  const { entreprisesBySiren } = useEntreprises();
  const { assujettisBySocietes } = useAssujetti();
  const { hasAllowedRole, hasAllowedAxeConformite } = useIsAllowed();
  const completeConformiteButtonRef = useRef<HTMLButtonElement>(null);

  const formConfig = useFormConfig<
    typeof assujettiInfoSchema,
    AssujettiInfoFormInput
  >({
    schema: assujettiInfoSchema,
    defaultValues: {
      id: assujetti?.id,
      email: assujetti?.email || undefined,
      name: assujetti?.name,
      role: assujetti?.role,
      sirenCompany: defaultSiren ?? assujetti?.sirenCompany,
      surname: assujetti?.surname,
      optionalAxesDescription: assujetti?.optionalAxesDescription || undefined,
      ...axesToDefaultValues(assujetti?.optionalAxes),
    },
  });

  const watchedAxeValues = axeFormFields.map((f) => formConfig.watch(f));
  const currentOptionalAxes = useMemo(
    () => getOptionalAxesFromValues(formConfig.getValues()),
    [formConfig, ...watchedAxeValues]
  );

  const hasAxesChanged = useMemo(
    () => !arraysEqual(currentOptionalAxes, assujetti?.optionalAxes ?? []),
    [currentOptionalAxes, assujetti?.optionalAxes]
  );

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    formConfig.setValue(
      "optionalAxesDescription",
      hasAxesChanged ? "" : assujetti?.optionalAxesDescription || ""
    );
  }, [formConfig, hasAxesChanged, assujetti?.optionalAxesDescription]);

  const selectedSiren = formConfig.watch("sirenCompany");
  const entreprise = entreprisesBySiren?.[selectedSiren];
  const availableStepsConformite = getAvailableStepsConformite(entreprise);

  const filteredAxeOptions = useMemo(
    () =>
      recordToSelectOptions(AxeConformiteAssujettiLabels).filter((option) => {
        const step = axeToStep[option.value];
        return (
          (!step || availableStepsConformite.includes(step)) &&
          hasAllowedAxeConformite(
            option.value as GatewayConformite.AxeConformite
          )
        );
      }),
    [availableStepsConformite, hasAllowedAxeConformite]
  );

  const handleSubmit = async (
    rawValues: AssujettiInfoFormInput,
    e: React.BaseSyntheticEvent | undefined
  ) => {
    // zodResolver applies the schema transform, so runtime values match AssujettiInfoFormValues
    const values = rawValues as unknown as AssujettiInfoFormValues;
    try {
      const submittedAssujetti = await GatewayAssujettiApi.set({
        millesime,
        ...values,
      });
      const newOptionalAxes = values.optionalAxes ?? [];
      afterSubmit(
        {
          ...submittedAssujetti,
          optionalAxes: submittedAssujetti.optionalAxes,
          avancement: computeAvancement(
            assujetti,
            submittedAssujetti,
            newOptionalAxes
          ),
        },
        (e?.nativeEvent as SubmitEvent).submitter ===
          completeConformiteButtonRef.current
      );
    } catch (err) {
      handleGatewayError({
        onUnhandled: () =>
          displayErrorToast(
            "Une erreur est survenue lors de la création de la personne"
          ),
      })(err);
      throw err;
    }
  };

  return (
    <Form formConfig={formConfig} onSubmit={handleSubmit}>
      <ReadonlyFieldSet readonly={readonly}>
        <FormGrid disableHorizontalGutter>
          <FormTitle>
            <h2>Informations de la personne</h2>
          </FormTitle>
          <FormNotice>
            <Emphasis>*</Emphasis> Champs obligatoires
          </FormNotice>
          {!defaultSiren && (
            <FormRow>
              <FormField>
                <InputSelect<AssujettiInfoFormInput, string>
                  label="Société"
                  name="sirenCompany"
                  options={companiesOption}
                  isSearchable
                  required
                />
              </FormField>
            </FormRow>
          )}
          <FormRow>
            <FormField>
              <InputText<AssujettiInfoFormInput>
                label="Nom"
                name="name"
                required
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <InputText<AssujettiInfoFormInput>
                label="Prénom"
                name="surname"
                required
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <InputSelect<
                AssujettiInfoFormInput,
                GatewayAssujetti.RoleAssujetti
              >
                label="Fonction"
                name="role"
                options={recordToSelectOptions(RoleAssujettiLabels)}
                required
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <InputText<AssujettiInfoFormInput>
                label="Adresse email"
                name="email"
              />
            </FormField>
          </FormRow>
          {assujetti &&
            entreprisesBySiren?.[assujetti.sirenCompany]
              ?.conformiteOptionnelle &&
            (assujettisBySocietes[assujetti.sirenCompany]?.length ?? 0) > 1 &&
            hasAllowedRole(
              GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR
            ) && (
              <>
                <FormRow>
                  <FormField>
                    <h4>Axes de conformité optionnels</h4>
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <InfoCard type="warning">
                      Toute modification des axes de conformité engendrera la
                      création d&apos;un ticket et fera l&apos;objet d&apos;une
                      vérification par l&apos;équipe Support d&apos;Endya.
                    </InfoCard>
                  </FormField>
                </FormRow>
                {filteredAxeOptions.map((option) => (
                  <FormRow key={option.value}>
                    <FormField>
                      <InputButtonGroup<AssujettiInfoFormInput, false, boolean>
                        size="medium"
                        label={option.label}
                        name={axeToAvancementField[option.value]}
                        variant="outline-pop"
                        options={obligatoireOptionnelOptions}
                      />
                    </FormField>
                  </FormRow>
                ))}
                {assujetti?.optionalAxesDescription && (
                  <FormRow>
                    <FormField>
                      <p className="input-container__label-text">
                        Justification précédente
                      </p>
                      <p className={styles.previousDescription}>
                        {assujetti.optionalAxesDescription}
                      </p>
                    </FormField>
                  </FormRow>
                )}
                {hasAxesChanged && (
                  <FormRow>
                    <FormField>
                      <InputTextArea<AssujettiInfoFormInput>
                        label="Justification"
                        name="optionalAxesDescription"
                        rows={4}
                        required
                      />
                    </FormField>
                  </FormRow>
                )}
              </>
            )}
          <FormAction className={styles.actions}>
            <Button
              type="submit"
              disabled={
                formConfig.formState.isSubmitting ||
                !formConfig.formState.isDirty
              }
            >
              Enregistrer
            </Button>
            <Button
              ref={completeConformiteButtonRef}
              variant="text"
              type="submit"
              iconVariant="left"
              icon={<ArrowRightIcon />}
              disabled={formConfig.formState.isSubmitting}
            >
              Compléter la conformite
            </Button>
          </FormAction>
        </FormGrid>
      </ReadonlyFieldSet>
    </Form>
  );
}
